import { EmptyState } from '@atlas/ui-components'
import type { AxisValueDTO } from '@/services/classification.service'

/**
 * Screen-specific recursive tree for hierarchical axes (species, geography,
 * indication). Not a design-system primitive — a taxonomy tree is specific to
 * this feature, so it lives with the Classification pages.
 */
interface ValueTreeProps {
	values: AxisValueDTO[]
	onRetire?: (valueId: string) => void
	onMerge?: (valueId: string) => void
	onAddChild?: (parentId: string) => void
}

interface Node extends AxisValueDTO {
	children: Node[]
}

function buildForest(values: AxisValueDTO[]): Node[] {
	const byId = new Map<string, Node>()
	for (const v of values) byId.set(v.valueId, { ...v, children: [] })
	const roots: Node[] = []
	for (const node of byId.values()) {
		const parent = node.parentId ? byId.get(node.parentId) : undefined
		if (parent) parent.children.push(node)
		else roots.push(node)
	}
	return roots
}

function TreeNode({
	node,
	depth,
	onRetire,
	onMerge,
	onAddChild,
}: { node: Node; depth: number } & Pick<ValueTreeProps, 'onRetire' | 'onMerge' | 'onAddChild'>) {
	const retired = node.status === 'retired' || node.validTo !== null
	return (
		<li>
			<div className="flex items-center gap-2 py-1" style={{ paddingLeft: `${depth * 1.25}rem` }}>
				<span className={`text-sm ${retired ? 'text-ds-text-subtle line-through' : 'font-medium'}`}>{node.label}</span>
				<span className="text-xs text-ds-text-subtle">{node.code}</span>
				{onAddChild && (
					<button
						type="button"
						className="text-xs text-ds-primary hover:underline"
						onClick={() => onAddChild(node.valueId)}
					>
						+ child
					</button>
				)}
				{onMerge && !retired && (
					<button
						type="button"
						className="text-ds-primary text-xs hover:underline"
						onClick={() => onMerge(node.valueId)}
					>
						merge
					</button>
				)}
				{onRetire && !retired && (
					<button
						type="button"
						className="text-xs text-ds-danger hover:underline"
						onClick={() => onRetire(node.valueId)}
					>
						retire
					</button>
				)}
			</div>
			{node.children.length > 0 && (
				<ul>
					{node.children.map((c) => (
						<TreeNode
							key={c.valueId}
							node={c}
							depth={depth + 1}
							onRetire={onRetire}
							onMerge={onMerge}
							onAddChild={onAddChild}
						/>
					))}
				</ul>
			)}
		</li>
	)
}

export function ValueTree({ values, onRetire, onMerge, onAddChild }: ValueTreeProps) {
	const forest = buildForest(values)
	if (forest.length === 0) return <EmptyState title="No values yet." />
	return (
		<ul>
			{forest.map((n) => (
				<TreeNode key={n.valueId} node={n} depth={0} onRetire={onRetire} onMerge={onMerge} onAddChild={onAddChild} />
			))}
		</ul>
	)
}

export { buildForest }
